// src/widgets/WeatherWidget.jsx
import { useState, useEffect } from 'react';
import { Cloud, CloudRain, CloudSnow, Sun, MapPin } from 'lucide-react';
import Empty from './Empty';

const WEATHER_CODE_MAP = {
  0:  { label: 'Cerah',              icon: Sun       },
  1:  { label: 'Cerah Berawan',      icon: Sun       },
  2:  { label: 'Berawan Sebagian',   icon: Cloud     },
  3:  { label: 'Berawan',            icon: Cloud     },
  45: { label: 'Berkabut',           icon: Cloud     },
  48: { label: 'Berkabut',           icon: Cloud     },
  51: { label: 'Gerimis Ringan',     icon: CloudRain },
  53: { label: 'Gerimis',            icon: CloudRain },
  55: { label: 'Gerimis Lebat',      icon: CloudRain },
  61: { label: 'Hujan Ringan',       icon: CloudRain },
  63: { label: 'Hujan',              icon: CloudRain },
  65: { label: 'Hujan Lebat',        icon: CloudRain },
  71: { label: 'Salju Ringan',       icon: CloudSnow },
  73: { label: 'Salju',              icon: CloudSnow },
  75: { label: 'Salju Lebat',        icon: CloudSnow },
  80: { label: 'Hujan Lokal',        icon: CloudRain },
  81: { label: 'Hujan Lokal Lebat',  icon: CloudRain },
  82: { label: 'Hujan Sangat Lebat', icon: CloudRain },
  95: { label: 'Badai Petir',        icon: CloudRain },
};

export default function WeatherWidget() {
  const [state, setState] = useState({ loading: true, error: null, data: null, place: null });

  useEffect(() => {
    let cancelled = false;

    const fetchWeather = async (lat, lon, place) => {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m`
        );
        const json = await res.json();
        if (cancelled) return;
        setState({ loading: false, error: null, data: json.current, place });
      } catch {
        if (cancelled) return;
        setState({ loading: false, error: 'Gagal memuat cuaca', data: null, place });
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => fetchWeather(pos.coords.latitude, pos.coords.longitude, null),
        ()    => fetchWeather(-6.2088, 106.8456, 'Jakarta'),
        { timeout: 5000 }
      );
    } else {
      fetchWeather(-6.2088, 106.8456, 'Jakarta');
    }

    return () => { cancelled = true; };
  }, []);

  const codeInfo = state.data
    ? (WEATHER_CODE_MAP[state.data.weather_code] || { label: 'Tidak diketahui', icon: Cloud })
    : null;
  const Icon = codeInfo?.icon || Cloud;

  return (
    <div style={{
      height: '100%',
      boxSizing: 'border-box',
      background: 'var(--bg-card)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 6,
      padding: 16,
      textAlign: 'center',
    }}>
      {state.loading ? (
        <div className="skeleton" style={{ width: '70%', height: 60 }} />
      ) : state.error ? (
        <Empty message={state.error} />
      ) : (
        <>
          <Icon size={28} color="var(--accent-orange)" />
          <div style={{
            fontSize: 30,
            fontWeight: 700,
            fontFamily: 'var(--font-mono)',
            color: 'var(--text-primary)',
          }}>
            {Math.round(state.data.temperature_2m)}°C
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            {codeInfo.label}
          </div>
          <div style={{
            fontSize: 10,
            color: 'var(--text-muted)',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}>
            <MapPin size={10} />
            {state.place || 'Lokasi Anda'} · Lembab {state.data.relative_humidity_2m}%
          </div>
        </>
      )}
    </div>
  );
}